import { Maximize2, Mic, MicOff, MonitorUp, PhoneOff, Video, VideoOff } from "lucide-react"
import { ControlButton } from "./ControlsBar"
import { PictureInPictureGrid } from "./PictureInPictureGrid"

type PictureInPictureWindowProps = {
  micOn: boolean
  camOn: boolean
  canSendAudio: boolean
  canSendVideo: boolean
  canShareScreen: boolean
  isSharingScreen: boolean
  onToggleMic: () => void
  onToggleCam: () => void
  onToggleScreenShare: () => void
  onReturn: () => void
  onLeave: () => void
}

/**
 * Compact call rendered into a document picture-in-picture window: everyone in a grid that
 * re-fits itself to the window (or a remote screen share with the people beneath it), the active
 * speaker outlined and a microphone badge per tile, plus the controls people reach for while
 * the tab is in the background. Rendered through a portal, so Daily hooks keep working. A click
 * in the floating window also counts as user activation for the opener, so the screen picker
 * can open from here (Document Picture-in-Picture spec, "user activation").
 */
export function PictureInPictureWindow({
  micOn,
  camOn,
  canSendAudio,
  canSendVideo,
  canShareScreen,
  isSharingScreen,
  onToggleMic,
  onToggleCam,
  onToggleScreenShare,
  onReturn,
  onLeave,
}: PictureInPictureWindowProps) {
  return (
    <div data-testid="call-pip-window" className="flex h-full w-full flex-col bg-[#0f1419] text-white">
      <div className="min-h-0 flex-1 p-2">
        <PictureInPictureGrid />
      </div>
      <div className="flex shrink-0 items-center justify-center gap-2 px-2 pb-2">
        <ControlButton label={micOn ? "Mute microphone" : "Unmute microphone"} active={micOn} disabled={!canSendAudio} onClick={onToggleMic} testId="call-pip-toggle-mic">
          {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </ControlButton>
        <ControlButton label={camOn ? "Turn camera off" : "Turn camera on"} active={camOn} disabled={!canSendVideo} onClick={onToggleCam} testId="call-pip-toggle-cam">
          {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
        </ControlButton>
        {canShareScreen && (
          <ControlButton label={isSharingScreen ? "Stop sharing screen" : "Share screen"} active={isSharingScreen} onClick={onToggleScreenShare} testId="call-pip-toggle-share">
            <MonitorUp className="h-5 w-5" />
          </ControlButton>
        )}
        <ControlButton label="Back to the call" onClick={onReturn} testId="call-pip-return">
          <Maximize2 className="h-5 w-5" />
        </ControlButton>
        <ControlButton label="Leave meeting" danger onClick={onLeave} testId="call-pip-leave">
          <PhoneOff className="h-5 w-5" />
        </ControlButton>
      </div>
    </div>
  )
}
